/**
 * @file pages/CustomersPage.js
 * @description Customer Butler — the customers list. Search, paginate, add a
 * customer (with the company's custom fields rendered dynamically), and click
 * through to the detail page. Matches the app's Apple-clean conventions.
 */
import React, { useState, useEffect, useCallback, useContext } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import AuthContext from '../contexts/AuthContext';
import { useTheme } from '../hooks/useTheme';
import { Modal } from '../design-system';
import { apiGet } from '../lib/api';
import { CustomFieldsForm } from '../components/CustomFieldsForm';
import { loadCustomers, createCustomerOffline } from '../offline/crm';
import { TagChips, useTagSuggestions } from '../components/TagEditor';
// WHY (2026-10-14, Honest UI, James via Fable): Add is offered only when the
// person's Customer Butler level (HR Team page) lets the server accept it.
import { useCustomerAccessView } from './customer/customerAccessView';

const STATUS_COLORS = {
  active: { bg: '#DCFCE7', text: '#166534' },
  prospect: { bg: '#FEF3C7', text: '#92400E' },
  inactive: { bg: '#F3F4F6', text: '#6B7280' },
  archived: { bg: '#FEE2E2', text: '#991B1B' },
};

function CustomersPage() {
  const auth = useContext(AuthContext);
  const { theme } = useTheme();
  const navigate = useNavigate();

  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [meta, setMeta] = useState({ total: 0, totalPages: 1 });
  const [showAdd, setShowAdd] = useState(false);
  const [tagFilter, setTagFilter] = useState('');
  const tagSuggestions = useTagSuggestions();
  const accessView = useCustomerAccessView(); // Look up = no Add

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // Offline-capable read-through cache: refreshes from server when online,
      // always serves from the local store (works identically with no signal).
      const { rows } = await loadCustomers({ search });
      setCustomers(rows);
      setMeta({ total: rows.length, totalPages: 1 });
    } catch (e) {
      console.error('Failed to load customers', e);
    } finally {
      setLoading(false);
    }
  }, [search]);

  useEffect(() => { load(); }, [load]);

  // Tag filtering happens on the rows we already hold, because this list is
  // served out of the offline cache - doing it server-side would break the
  // filter the moment the truck loses signal. The dropdown offers the company's
  // suggestion list PLUS any free-form tag actually in use, so a tag somebody
  // invented this morning is filterable this afternoon.
  const tagsInUse = customers.reduce((acc, c) => acc.concat(c.tags || []), []);
  const tagOptions = Array.from(new Set([...tagSuggestions, ...tagsInUse]))
    .sort((a, b) => a.localeCompare(b));
  const visible = tagFilter
    ? customers.filter((c) => (c.tags || []).some((t) => t.toLowerCase() === tagFilter.toLowerCase()))
    : customers;

  if (!auth.isAuthenticated) return <Navigate to="/login" />;

  return (
    <div className="page-content" style={{ padding: '1rem', maxWidth: '1100px', margin: '0 auto' }}>
      <div style={styles.header}>
        <h2 style={{ margin: 0 }}>Customers</h2>
        {accessView.mayWrite ? (
          <button className="btn btn-primary" style={{ ...styles.btnPrimary, backgroundColor: theme.accentColor }} onClick={() => setShowAdd(true)}>
            + Add Customer
          </button>
        ) : null}
      </div>

      <div style={{ marginBottom: '1rem', display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center' }}>
        <div style={{ flex: '1 1 260px' }}>
        <input
          style={styles.search}
          placeholder="Search by name, email, phone, company…"
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(1); }}
        />
        </div>
        <select
          style={{
            padding: '0.6rem 0.5rem',
            border: '1px solid var(--color-border, #E5E7EB)',
            borderRadius: '8px',
            fontSize: '0.9rem',
            backgroundColor: 'var(--surface-card, #fff)',
            color: 'var(--text-primary, #111)',
          }}
          value={tagFilter}
          onChange={(e) => { setTagFilter(e.target.value); setPage(1); }}
          aria-label="Filter customers by tag"
        >
          <option value="">All tags</option>
          {tagOptions.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
      </div>

      {loading ? (
        <div style={styles.muted}>Loading…</div>
      ) : visible.length === 0 ? (
        <div style={styles.empty}>
          <h3>{search || tagFilter ? 'No matches' : 'No customers yet'}</h3>
          <p style={{ color: 'var(--text-secondary, #666)' }}>
            {search || tagFilter ? 'Try a different search or tag.' : 'Add your first customer to get started.'}
          </p>
        </div>
      ) : (
        <>
          <div style={styles.tableWrap}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Name</th>
                  <th style={styles.thHide}>Email</th>
                  <th style={styles.thHide}>Phone</th>
                  <th style={styles.thHide}>Tags</th>
                  <th style={styles.th}>Status</th>
                </tr>
              </thead>
              <tbody>
                {visible.map((c) => {
                  const sc = STATUS_COLORS[c.status] || STATUS_COLORS.inactive;
                  return (
                    <tr key={c.id} style={styles.tr} onClick={() => { if (c._syncStatus !== 'pending') navigate(`/customers/${c.id}`); }}>
                      <td style={styles.td}>
                        <strong>{c.displayName}</strong>
                        {c.type === 'company' && <span style={styles.tag}>Company</span>}
                        {c._syncStatus === 'pending' && <span style={styles.pending} title="Saved on this device — syncs when you're back online">● not synced</span>}
                      </td>
                      <td style={styles.tdHide}>{c.primaryEmail || '-'}</td>
                      <td style={styles.tdHide}>{c.primaryPhone || '-'}</td>
                      <td style={styles.tdHide}><TagChips tags={c.tags} max={3} /></td>
                      <td style={styles.td}>
                        <span style={{ ...styles.badge, backgroundColor: sc.bg, color: sc.text }}>{c.status}</span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {meta.totalPages > 1 && (
            <div style={styles.pager}>
              <button style={styles.pageBtn} disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>← Prev</button>
              <span style={styles.muted}>Page {page} of {meta.totalPages} ({meta.total} total)</span>
              <button style={styles.pageBtn} disabled={page >= meta.totalPages} onClick={() => setPage((p) => p + 1)}>Next →</button>
            </div>
          )}
        </>
      )}

      <Modal isOpen={showAdd} onClose={() => setShowAdd(false)} title="Add Customer">
        <AddCustomerForm
          onCancel={() => setShowAdd(false)}
          onSaved={() => { setShowAdd(false); load(); }}
        />
      </Modal>
    </div>
  );
}

function AddCustomerForm({ onCancel, onSaved }) {
  const [form, setForm] = useState({ type: 'person', firstName: '', lastName: '', companyName: '', primaryEmail: '', primaryPhone: '', status: 'active', source: '' });
  const [defs, setDefs] = useState([]);
  const [customFields, setCustomFields] = useState({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    apiGet('/customer/field-definitions?entityType=Customer&activeOnly=true').then(setDefs).catch(() => setDefs([]));
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  async function submit(e) {
    e.preventDefault();
    setSaving(true); setError(null);
    try {
      // Offline-capable: writes locally + queues for sync. Works with no signal.
      await createCustomerOffline({ ...form, customFields });
      setSaving(false);
      onSaved();
    } catch (err) {
      setSaving(false);
      setError('Failed to save customer');
    }
  }

  return (
    <form onSubmit={submit} style={{ padding: '1rem' }}>
      {error && <div style={styles.error}>{error}</div>}
      <fieldset style={styles.fieldset}>
        <legend style={styles.legend}>Customer</legend>
        <div style={styles.row}>
          <div style={styles.field}>
            <label style={styles.label}>Type</label>
            <select style={styles.input} value={form.type} onChange={(e) => set('type', e.target.value)}>
              <option value="person">Person</option>
              <option value="company">Company</option>
            </select>
          </div>
          <div style={styles.field}>
            <label style={styles.label}>Status</label>
            <select style={styles.input} value={form.status} onChange={(e) => set('status', e.target.value)}>
              <option value="active">Active</option>
              <option value="prospect">Prospect</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
        </div>
        {form.type === 'company' ? (
          <div style={styles.field}>
            <label style={styles.label}>Company Name *</label>
            <input style={styles.input} value={form.companyName} onChange={(e) => set('companyName', e.target.value)} required autoFocus />
          </div>
        ) : (
          <div style={styles.row}>
            <div style={styles.field}>
              <label style={styles.label}>First Name</label>
              <input style={styles.input} value={form.firstName} onChange={(e) => set('firstName', e.target.value)} autoFocus />
            </div>
            <div style={styles.field}>
              <label style={styles.label}>Last Name</label>
              <input style={styles.input} value={form.lastName} onChange={(e) => set('lastName', e.target.value)} />
            </div>
          </div>
        )}
        <div style={styles.row}>
          <div style={styles.field}>
            <label style={styles.label}>Email</label>
            <input style={styles.input} type="email" value={form.primaryEmail} onChange={(e) => set('primaryEmail', e.target.value)} />
          </div>
          <div style={styles.field}>
            <label style={styles.label}>Phone</label>
            <input style={styles.input} type="tel" value={form.primaryPhone} onChange={(e) => set('primaryPhone', e.target.value)} />
          </div>
        </div>
      </fieldset>

      <CustomFieldsForm definitions={defs} values={customFields} onChange={(k, v) => setCustomFields((c) => ({ ...c, [k]: v }))} />

      <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
        <button type="button" style={styles.btnSecondary} onClick={onCancel}>Cancel</button>
        <button type="submit" disabled={saving} style={{ ...styles.btnPrimary, opacity: saving ? 0.6 : 1 }}>
          {saving ? 'Saving…' : 'Create Customer'}
        </button>
      </div>
    </form>
  );
}

const styles = {
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', flexWrap: 'wrap', gap: '0.5rem' },
  search: { width: '100%', maxWidth: '420px', padding: '0.5rem 0.75rem', border: '1px solid var(--color-border, #E5E7EB)', borderRadius: '8px', fontSize: '0.9rem', backgroundColor: 'var(--surface-card, #fff)', color: 'var(--text-primary, #111)', boxSizing: 'border-box' },
  tableWrap: { border: '1px solid var(--color-border, #E5E7EB)', borderRadius: '8px', overflow: 'auto', backgroundColor: 'var(--surface-card, #fff)' },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' },
  th: { padding: '0.75rem', textAlign: 'left', borderBottom: '2px solid var(--color-border, #E5E7EB)', fontWeight: 600, fontSize: '0.7rem', textTransform: 'uppercase', color: 'var(--text-secondary, #6B7280)' },
  thHide: { padding: '0.75rem', textAlign: 'left', borderBottom: '2px solid var(--color-border, #E5E7EB)', fontWeight: 600, fontSize: '0.7rem', textTransform: 'uppercase', color: 'var(--text-secondary, #6B7280)' },
  tr: { borderBottom: '1px solid var(--color-border, #E5E7EB)', cursor: 'pointer' },
  td: { padding: '0.75rem', color: 'var(--text-primary, #111)' },
  tdHide: { padding: '0.75rem', color: 'var(--text-secondary, #444)' },
  tag: { marginLeft: '0.5rem', fontSize: '0.65rem', padding: '1px 6px', borderRadius: '4px', backgroundColor: 'var(--surface-page, #F3F4F6)', color: 'var(--text-secondary, #6B7280)' },
  pending: { marginLeft: '0.5rem', fontSize: '0.62rem', padding: '1px 6px', borderRadius: '999px', backgroundColor: '#FEF3C7', color: '#8a5800', fontWeight: 600 },
  badge: { padding: '0.2rem 0.5rem', borderRadius: '9999px', fontSize: '0.72rem', fontWeight: 500, textTransform: 'capitalize' },
  pager: { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '1rem', marginTop: '1rem' },
  pageBtn: { padding: '0.4rem 0.75rem', border: '1px solid var(--color-border, #E5E7EB)', borderRadius: '6px', backgroundColor: 'var(--surface-card, #fff)', cursor: 'pointer', fontSize: '0.8rem' },
  muted: { color: 'var(--text-secondary, #666)', fontSize: '0.875rem', textAlign: 'center' },
  empty: { textAlign: 'center', padding: '3rem', border: '2px dashed var(--color-border, #E5E7EB)', borderRadius: '8px' },
  fieldset: { border: '1px solid var(--color-border, #E5E7EB)', borderRadius: '6px', padding: '0.75rem', marginBottom: '1rem' },
  legend: { fontWeight: 600, fontSize: '0.875rem', padding: '0 0.5rem' },
  row: { display: 'flex', gap: '0.75rem', flexWrap: 'wrap' },
  field: { flex: 1, minWidth: '140px', marginBottom: '0.5rem' },
  label: { display: 'block', fontSize: '0.75rem', fontWeight: 500, marginBottom: '0.25rem', color: 'var(--text-secondary, #6B7280)' },
  input: { width: '100%', padding: '0.5rem', border: '1px solid var(--color-border, #E5E7EB)', borderRadius: '6px', fontSize: '0.875rem', backgroundColor: 'var(--surface-card, #fff)', color: 'var(--text-primary, #111)', boxSizing: 'border-box' },
  btnPrimary: { padding: '0.5rem 1rem', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '0.875rem', fontWeight: 600, backgroundColor: 'var(--color-primary-600, #2563EB)', color: '#fff' },
  btnSecondary: { padding: '0.5rem 1rem', border: '1px solid var(--color-border, #E5E7EB)', borderRadius: '6px', backgroundColor: 'var(--surface-card, #fff)', cursor: 'pointer', fontSize: '0.875rem', fontWeight: 500 },
  error: { backgroundColor: '#FEE2E2', color: '#991B1B', padding: '0.5rem 0.75rem', borderRadius: '6px', marginBottom: '1rem', fontSize: '0.85rem' },
};

export default CustomersPage;
